import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { weeklyEventSheetsAPI, recurringWeeklyEventsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Calendar as CalendarIcon, Plus, Printer, Save, Trash2, Loader2,
  RefreshCw, GripVertical, Repeat,
} from 'lucide-react';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SERVICE_STYLES = ['buffet', 'plated', 'family_style', 'stations', 'stationed_reception', 'combination', 'cocktail_only'];
const ROOMS = ['Pub', 'Banquet Hall', 'Lower Patio', 'Upper Patio', 'Ceremony Space', 'Rustic Ranch', 'Offsite', 'TBD'];

/* Snap any date string (YYYY-MM-DD) to the Sunday of that ISO week */
const snapToSunday = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  const dow = d.getDay();
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
};

const formatLongDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  // Date only — weekday is rendered separately as the bold day name.
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

/* "September 28th" style */
const formatPrintDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const month = d.toLocaleDateString(undefined, { month: 'long' });
  const day = d.getDate();
  const j = day % 10, k = day % 100;
  const suffix = (k >= 11 && k <= 13) ? 'th' : (j === 1 ? 'st' : j === 2 ? 'nd' : j === 3 ? 'rd' : 'th');
  return `${month} ${day}${suffix}`;
};

const addDays = (iso, days) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/* "17:30" → "5:30pm"; empty stays empty */
const formatTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  if (Number.isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return m ? `${h}:${m.toString().padStart(2, '0')}${ampm}` : `${h}${ampm}`;
};

const formatTimeRange = (start, end) => {
  if (!start && !end) return '';
  if (start && end) return `${formatTime12(start)} – ${formatTime12(end)}`;
  return formatTime12(start || end);
};

const newRow = (dayIndex) => ({
  id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  day_index: dayIndex,
  order: 0,
  event_name: '',
  time_start: '',
  time_end: '',
  guest_count: null,
  room: '',
  service_style: '',
  notes: '',
  source: 'manual',
  source_id: null,
});

const WeeklyEventSheet = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [weekStart, setWeekStart] = useState(snapToSunday(today));
  const [sheetId, setSheetId] = useState(null);
  const [title, setTitle] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [showRecurringPanel, setShowRecurringPanel] = useState(false);
  const [selectedRecurringIds, setSelectedRecurringIds] = useState({});
  const [showManageRecurring, setShowManageRecurring] = useState(false);
  const [editingRecurringId, setEditingRecurringId] = useState(null);
  const emptyRecurringForm = {
    name: '', day_index: 1, time_start: '', time_end: '',
    guest_count: '', room: '', service_style: '', notes: '', active: true, sort_order: 0,
  };
  const [recurringForm, setRecurringForm] = useState(emptyRecurringForm);
  const [savingRecurring, setSavingRecurring] = useState(false);

  useEffect(() => { loadSheet(weekStart); /* eslint-disable-next-line */ }, [weekStart]);
  useEffect(() => { refreshRecurring(); }, []);

  const refreshRecurring = async () => {
    try {
      const data = await recurringWeeklyEventsAPI.list();
      setRecurringEvents(data || []);
    } catch { /* ignore */ }
  };

  const loadSheet = async (ws) => {
    try {
      setLoading(true);
      const sheet = await weeklyEventSheetsAPI.byWeek(ws);
      if (sheet) {
        setSheetId(sheet.id);
        setTitle(sheet.title || '');
        setItems(sheet.items || []);
      } else {
        setSheetId(null);
        setTitle('');
        setItems([]);
      }
    } catch (e) {
      toast({ title: 'Error loading sheet', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const recurringByDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    (recurringEvents || []).filter(r => r.active !== false).forEach(r => {
      if (r.day_index >= 0 && r.day_index <= 6) map[r.day_index].push(r);
    });
    map.forEach(arr => arr.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    return map;
  }, [recurringEvents]);

  const toggleRecurringSelection = (id) => {
    setSelectedRecurringIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const addSelectedRecurringToWeek = () => {
    const selected = recurringEvents.filter(r => selectedRecurringIds[r.id]);
    if (selected.length === 0) {
      toast({ title: 'Nothing selected', description: 'Check at least one recurring event.', variant: 'destructive' });
      return;
    }
    const newRows = selected.map((r, i) => ({
      ...newRow(r.day_index),
      event_name: r.name,
      time_start: r.time_start || '',
      time_end: r.time_end || '',
      guest_count: r.guest_count ?? null,
      room: r.room || '',
      service_style: r.service_style || '',
      notes: r.notes || '',
      source: 'manual',
      order: 5000 + i,
    }));
    setItems(prev => reorderWithinDays([...prev, ...newRows]));
    setSelectedRecurringIds({});
    setShowRecurringPanel(false);
    toast({ title: `Added ${newRows.length} recurring event(s)`, description: 'Remember to Save the sheet.' });
  };

  const openCreateRecurring = () => {
    setEditingRecurringId(null);
    setRecurringForm(emptyRecurringForm);
    setShowManageRecurring(true);
  };

  const openEditRecurring = (r) => {
    setEditingRecurringId(r.id);
    setRecurringForm({
      name: r.name || '',
      day_index: r.day_index ?? 1,
      time_start: r.time_start || '',
      time_end: r.time_end || '',
      guest_count: r.guest_count ?? '',
      room: r.room || '',
      service_style: r.service_style || '',
      notes: r.notes || '',
      active: r.active !== false,
      sort_order: r.sort_order ?? 0,
    });
    setShowManageRecurring(true);
  };

  const saveRecurring = async () => {
    if (!recurringForm.name?.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    try {
      setSavingRecurring(true);
      const payload = {
        ...recurringForm,
        day_index: Number(recurringForm.day_index),
        guest_count: recurringForm.guest_count === '' || recurringForm.guest_count == null
          ? null : Number(recurringForm.guest_count),
        sort_order: Number(recurringForm.sort_order || 0),
      };
      if (editingRecurringId) {
        await recurringWeeklyEventsAPI.update(editingRecurringId, payload);
        toast({ title: 'Recurring event updated' });
      } else {
        await recurringWeeklyEventsAPI.create(payload);
        toast({ title: 'Recurring event added' });
      }
      await refreshRecurring();
      setShowManageRecurring(false);
      setEditingRecurringId(null);
      setRecurringForm(emptyRecurringForm);
    } catch (e) {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally {
      setSavingRecurring(false);
    }
  };

  const deleteRecurring = async (id) => {
    if (!window.confirm('Delete this recurring event?')) return;
    try {
      await recurringWeeklyEventsAPI.remove(id);
      toast({ title: 'Deleted' });
      await refreshRecurring();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const autoPopulate = async () => {
    try {
      setAutoLoading(true);
      const data = await weeklyEventSheetsAPI.autoPopulate(weekStart);
      const pulled = data?.items || [];
      // Avoid duplicates: skip rows where source_id already present in current items
      const existingSourceIds = new Set(items.filter(i => i.source_id).map(i => i.source_id));
      const fresh = pulled.filter(p => !existingSourceIds.has(p.source_id));
      if (fresh.length === 0) {
        toast({ title: 'No new events to add for this week.' });
      } else {
        // Append, then re-sort within each day
        const merged = [...items, ...fresh.map((p, i) => ({ ...p, order: 1000 + i }))];
        setItems(reorderWithinDays(merged));
        toast({ title: `Pulled ${fresh.length} event(s) from contracts/BEOs.` });
      }
    } catch (e) {
      toast({ title: 'Auto-populate failed', variant: 'destructive' });
    } finally {
      setAutoLoading(false);
    }
  };

  /* Sort items by day_index then by their `order`, then assign sequential `order` per day */
  const reorderWithinDays = (list) => {
    const byDay = {};
    list.forEach(it => {
      const d = it.day_index;
      byDay[d] = byDay[d] || [];
      byDay[d].push(it);
    });
    Object.keys(byDay).forEach(d => {
      byDay[d].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      byDay[d] = byDay[d].map((it, idx) => ({ ...it, order: idx }));
    });
    const out = [];
    for (let d = 0; d <= 6; d += 1) (byDay[d] || []).forEach(it => out.push(it));
    return out;
  };

  const itemsByDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).forEach(it => {
      if (it.day_index >= 0 && it.day_index <= 6) map[it.day_index].push(it);
    });
    return map;
  }, [items]);

  const addRow = (dayIndex) => {
    const row = newRow(dayIndex);
    row.order = (itemsByDay[dayIndex]?.length || 0);
    setItems(prev => [...prev, row]);
  };

  const updateRow = (id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeRow = (id) => {
    setItems(prev => reorderWithinDays(prev.filter(it => it.id !== id)));
  };

  const onDragStart = (id) => setDraggedId(id);
  const onDragOver = (e) => { e.preventDefault(); };
  const onDropOnRow = (targetId) => {
    if (!draggedId || draggedId === targetId) { setDraggedId(null); return; }
    setItems(prev => {
      const dragged = prev.find(i => i.id === draggedId);
      const target = prev.find(i => i.id === targetId);
      if (!dragged || !target) return prev;
      // Move dragged into target's day & immediately above target
      const without = prev.filter(i => i.id !== draggedId);
      const targetIdx = without.findIndex(i => i.id === targetId);
      const updatedDragged = { ...dragged, day_index: target.day_index };
      const next = [...without.slice(0, targetIdx), updatedDragged, ...without.slice(targetIdx)];
      return reorderWithinDays(next);
    });
    setDraggedId(null);
  };
  const onDropOnDay = (dayIndex) => {
    if (!draggedId) return;
    setItems(prev => {
      const dragged = prev.find(i => i.id === draggedId);
      if (!dragged) return prev;
      const without = prev.filter(i => i.id !== draggedId);
      const updatedDragged = { ...dragged, day_index: dayIndex, order: 9999 };
      return reorderWithinDays([...without, updatedDragged]);
    });
    setDraggedId(null);
  };

  const save = async () => {
    try {
      setSaving(true);
      const cleaned = items.map(it => ({
        ...it,
        guest_count: it.guest_count === '' || it.guest_count == null ? null : parseInt(it.guest_count) || null,
      }));
      const payload = { week_start: weekStart, title, items: cleaned };
      if (sheetId) {
        const updated = await weeklyEventSheetsAPI.update(sheetId, { title, items: cleaned });
        setItems(updated.items || []);
      } else {
        const created = await weeklyEventSheetsAPI.create(payload);
        setSheetId(created.id);
        setItems(created.items || []);
      }
      toast({ title: 'Weekly sheet saved' });
    } catch (e) {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deleteSheet = async () => {
    if (!sheetId) return;
    if (!window.confirm('Delete this weekly sheet?')) return;
    try {
      await weeklyEventSheetsAPI.remove(sheetId);
      setSheetId(null);
      setTitle('');
      setItems([]);
      toast({ title: 'Sheet deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const print = () => window.print();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <style>{`
        @media print {
          @page { size: portrait; margin: 0.6in; }
          .no-print { display: none !important; }
          body, html { background: white !important; font-family: 'Calibri', 'Helvetica', sans-serif; }
          .print-page { padding: 0 !important; }
          /* Drop the Card chrome on day cards */
          .print-day { 
            break-inside: avoid; page-break-inside: avoid;
            border: none !important; box-shadow: none !important; 
            margin: 0 0 14px 0 !important;
          }
          .print-day [class*="CardContent"], .print-day .pt-4 {
            padding: 0 !important; margin: 0 !important;
          }
          .print-day-header {
            margin: 0 0 4px 0 !important; padding: 0 !important;
            color: #000;
          }
          .print-day-name {
            font-size: 17px !important; font-weight: 700;
          }
          .print-day-date {
            font-size: 14px !important; font-weight: 400;
            margin-left: 8px;
          }
          .print-event {
            padding: 4px 0 !important;
            border: none !important; background: transparent !important;
            font-size: 14px !important; line-height: 1.5 !important;
            margin: 0 !important;
          }
          .print-event-name { font-weight: 700; }
          .print-event-meta { font-weight: 400; }
          .print-event-notes { font-style: italic; color: #333; }
          .print-no-events {
            font-size: 14px !important; font-style: italic;
            padding: 4px 0 !important; color: #555;
          }
          /* Top title block */
          .print-title {
            font-size: 26px !important; font-weight: 700;
            text-align: center; letter-spacing: 1px;
            margin: 0 0 6px 0 !important;
          }
          .print-subtitle {
            font-size: 13px !important; text-align: center;
            margin-bottom: 14px !important; color: #444;
          }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto">
        {/* Header (no-print) */}
        <div className="mb-6 no-print">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CalendarIcon className="text-[#1e3a5f]" size={28} />
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Weekly Event Sheet</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowRecurringPanel(s => !s)}
                variant="outline"
                data-testid="wes-recurring-toggle"
              >
                <Repeat size={16} className="mr-1" />Recurring Events
              </Button>
              <Button onClick={print} variant="outline" data-testid="wes-print-btn">
                <Printer size={16} className="mr-1" />Print
              </Button>
              <Button
                onClick={() => navigate('/club-services-rollup?date=' + weekStart)}
                variant="outline"
                className="border-[#c9a227] text-[#8b7355] hover:bg-[#faf6ea]"
                data-testid="wes-rollup-btn"
                title="Print a driver-friendly roll-up for days with 2+ offsite events"
              >
                Club Services Roll-Up
              </Button>
              {sheetId && (
                <Button onClick={deleteSheet} variant="outline" className="text-red-600 hover:bg-red-50" data-testid="wes-delete-btn">
                  <Trash2 size={16} />
                </Button>
              )}
              <Button onClick={save} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="wes-save-btn">
                {saving ? (<><Loader2 size={16} className="animate-spin mr-1" />Saving…</>) : (<><Save size={16} className="mr-1" />Save</>)}
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Week of (Sunday)</Label>
                  <Input
                    type="date"
                    value={weekStart}
                    onChange={(e) => setWeekStart(snapToSunday(e.target.value))}
                    data-testid="wes-week-start"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Title (optional)</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Week of Feb 1 - Wedding-heavy"
                    data-testid="wes-title"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button onClick={autoPopulate} disabled={autoLoading} variant="outline" data-testid="wes-auto-populate-btn">
                  {autoLoading ? (<><Loader2 size={16} className="animate-spin mr-1" />Pulling…</>) : (<><RefreshCw size={16} className="mr-1" />Auto-populate from Contracts/BEOs</>)}
                </Button>
                <span className="text-xs text-gray-500">Pulls events with <code>event_date</code> in {weekStart} → {addDays(weekStart, 6)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recurring Events panel (toggleable) */}
        {showRecurringPanel && (
          <Card className="mb-6 no-print" data-testid="wes-recurring-panel">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Repeat size={18} className="text-[#1e3a5f]" />
                  <h3 className="text-base font-bold text-[#1e3a5f]">Recurring Weekly Events</h3>
                  <span className="text-xs text-gray-500">Check any to quickly add to this week.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={openCreateRecurring}
                    size="sm"
                    variant="outline"
                    data-testid="wes-manage-recurring-btn"
                  >
                    <Plus size={14} className="mr-1" />New / Manage
                  </Button>
                  <Button
                    onClick={addSelectedRecurringToWeek}
                    size="sm"
                    className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
                    data-testid="wes-recurring-add-to-sheet"
                  >
                    <Plus size={14} className="mr-1" />Add Selected to Sheet
                  </Button>
                </div>
              </div>

              {recurringEvents.length === 0 ? (
                <p className="text-sm text-gray-500 italic py-2">
                  No recurring events yet. Click "New / Manage" to create one (e.g., "Mens League — Thursday 12pm-6pm").
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {DAY_NAMES.map((dn, di) => {
                    const list = recurringByDay[di];
                    if (!list || list.length === 0) return null;
                    return (
                      <div key={di} className="border rounded p-2 bg-gray-50">
                        <div className="text-xs font-semibold text-[#1e3a5f] mb-1">{dn}</div>
                        <div className="space-y-1">
                          {list.map(r => (
                            <div
                              key={r.id}
                              className="flex items-start gap-2 text-sm"
                              data-testid={`wes-recurring-row-${r.id}`}
                            >
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!selectedRecurringIds[r.id]}
                                onChange={() => toggleRecurringSelection(r.id)}
                                data-testid={`wes-recurring-checkbox-${r.id}`}
                              />
                              <div className="flex-1">
                                <div className="font-medium">{r.name}</div>
                                <div className="text-xs text-gray-600">
                                  {formatTimeRange(r.time_start, r.time_end) || '—'}
                                  {r.guest_count ? ` · ${r.guest_count}ppl` : ''}
                                  {r.room ? ` · ${r.room}` : ''}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="text-xs text-blue-600 hover:underline"
                                onClick={() => openEditRecurring(r)}
                                data-testid={`wes-recurring-edit-${r.id}`}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-xs text-red-600 hover:underline"
                                onClick={() => deleteRecurring(r.id)}
                                data-testid={`wes-recurring-delete-${r.id}`}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {/* Inactive list */}
                  {recurringEvents.some(r => r.active === false) && (
                    <div className="border rounded p-2 bg-gray-50 opacity-70">
                      <div className="text-xs font-semibold text-gray-500 mb-1">Inactive</div>
                      <div className="space-y-1">
                        {recurringEvents.filter(r => r.active === false).map(r => (
                          <div key={r.id} className="flex items-center gap-2 text-sm">
                            <div className="flex-1">
                              <div className="font-medium line-through">{r.name}</div>
                              <div className="text-xs text-gray-500">{DAY_NAMES[r.day_index] || ''}</div>
                            </div>
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => openEditRecurring(r)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => deleteRecurring(r.id)}
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Print header (visible only when printing) */}
        <div className="hidden print:block print-page">
          <h1 className="print-title">WEEKLY EVENTS</h1>
          <p className="print-subtitle">
            Week of {formatPrintDate(weekStart)} – {formatPrintDate(addDays(weekStart, 6))}
            {title ? ` · ${title}` : ''}
          </p>
        </div>

        {/* Days (agenda layout) */}
        <div className="space-y-4 print:space-y-0 print-page">
          {loading ? (
            <Card><CardContent className="py-12 text-center"><Loader2 className="animate-spin inline mr-2" />Loading…</CardContent></Card>
          ) : DAY_NAMES.map((dayName, dayIdx) => {
            const dayItems = itemsByDay[dayIdx];
            const dateStr = formatLongDate(addDays(weekStart, dayIdx));
            const printDateStr = formatPrintDate(addDays(weekStart, dayIdx));
            const isEmpty = dayItems.length === 0;
            return (
              <Card
                key={dayIdx}
                className="print-day"
                onDragOver={onDragOver}
                onDrop={() => onDropOnDay(dayIdx)}
                data-testid={`wes-day-${dayIdx}`}
              >
                <CardContent className="pt-4">
                  {/* On-screen day header (no-print) */}
                  <div className="flex items-baseline justify-between mb-2 border-b pb-2 no-print">
                    <h2 className="text-lg font-bold text-[#1e3a5f]">
                      {dayName} <span className="text-sm font-normal text-gray-500">— {dateStr}</span>
                    </h2>
                    <Button
                      onClick={() => addRow(dayIdx)}
                      size="sm"
                      variant="outline"
                      data-testid={`wes-add-row-${dayIdx}`}
                    >
                      <Plus size={14} className="mr-1" />Add Event
                    </Button>
                  </div>
                  {/* Print-only day header: name + date stacked */}
                  <div className="hidden print:block print-day-header">
                    <span className="print-day-name">{dayName}</span>
                    <span className="print-day-date">{printDateStr}</span>
                  </div>
                  {/* Print-only "No Events" row when day is empty */}
                  {isEmpty && (
                    <div className="hidden print:block print-no-events">No Events</div>
                  )}

                  {dayItems.length === 0 ? (
                    <p className="text-sm text-gray-400 italic py-2 no-print">No events. Click "Add Event" or "Auto-populate".</p>
                  ) : (
                    <div className="space-y-3">
                      {dayItems.map((it) => (
                        <div
                          key={it.id}
                          draggable={true}
                          onDragStart={() => onDragStart(it.id)}
                          onDragOver={onDragOver}
                          onDrop={(e) => { e.stopPropagation(); onDropOnRow(it.id); }}
                          className={`bg-gray-50 border rounded-lg p-3 print-event ${draggedId === it.id ? 'opacity-50' : ''}`}
                          data-testid={`wes-row-${it.id}`}
                        >
                          {/* Editable fields (no-print) */}
                          <div className="grid grid-cols-12 gap-2 items-start no-print">
                            <div className="col-span-1 flex items-center pt-2 cursor-move text-gray-400" title="Drag to reorder">
                              <GripVertical size={18} />
                            </div>
                            <div className="col-span-11 grid grid-cols-12 gap-2">
                              <div className="col-span-12 sm:col-span-5">
                                <Label className="text-[10px] uppercase text-gray-500">Event Name</Label>
                                <Input
                                  value={it.event_name || ''}
                                  onChange={(e) => updateRow(it.id, { event_name: e.target.value })}
                                  placeholder="Smith Wedding"
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">Start</Label>
                                <Input
                                  type="time"
                                  value={it.time_start || ''}
                                  onChange={(e) => updateRow(it.id, { time_start: e.target.value })}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">End</Label>
                                <Input
                                  type="time"
                                  value={it.time_end || ''}
                                  onChange={(e) => updateRow(it.id, { time_end: e.target.value })}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">Guests</Label>
                                <Input
                                  type="number" min="0"
                                  value={it.guest_count ?? ''}
                                  onChange={(e) => updateRow(it.id, { guest_count: e.target.value === '' ? null : parseInt(e.target.value) || 0 })}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-1 flex items-end justify-end">
                                <Button
                                  onClick={() => removeRow(it.id)}
                                  variant="ghost" size="sm"
                                  className="text-red-600 hover:bg-red-50"
                                  title="Remove"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                              <div className="col-span-12 sm:col-span-6">
                                <Label className="text-[10px] uppercase text-gray-500">Room</Label>
                                <Select value={it.room || ''} onValueChange={(v) => updateRow(it.id, { room: v })}>
                                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                  <SelectContent>
                                    {ROOMS.map(r => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-12 sm:col-span-6">
                                <Label className="text-[10px] uppercase text-gray-500">Service Style</Label>
                                <Select value={it.service_style || ''} onValueChange={(v) => updateRow(it.id, { service_style: v })}>
                                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                  <SelectContent>
                                    {SERVICE_STYLES.map(s => (<SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-12">
                                <Label className="text-[10px] uppercase text-gray-500">Notes</Label>
                                <Textarea
                                  rows={2}
                                  value={it.notes || ''}
                                  onChange={(e) => updateRow(it.id, { notes: e.target.value })}
                                />
                              </div>
                              {it.source !== 'manual' && (
                                <div className="col-span-12 text-xs text-gray-500 italic">
                                  Pulled from {it.source === 'contract' ? 'Contract' : 'Standalone BEO'}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Print-only narrative line: Name – time – Nppl – room – style – notes */}
                          <div className="hidden print:block">
                            {(() => {
                              const parts = [];
                              const time = formatTimeRange(it.time_start, it.time_end);
                              if (time) parts.push(time);
                              if (it.guest_count != null && it.guest_count !== '') parts.push(`${it.guest_count}ppl`);
                              if (it.room) parts.push(it.room);
                              if (it.service_style) parts.push(it.service_style.replace(/_/g, ' '));
                              return (
                                <>
                                  <span className="print-event-name">{it.event_name || '—'}</span>
                                  {parts.length > 0 && (
                                    <span className="print-event-meta"> – {parts.join(' – ')}</span>
                                  )}
                                  {it.notes && (
                                    <span className="print-event-notes"> – {it.notes}</span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Manage Recurring Event dialog */}
      <Dialog open={showManageRecurring} onOpenChange={(open) => { setShowManageRecurring(open); if (!open) { setEditingRecurringId(null); setRecurringForm(emptyRecurringForm); } }}>
        <DialogContent className="max-w-lg" data-testid="wes-manage-recurring-dialog">
          <DialogHeader>
            <DialogTitle>{editingRecurringId ? 'Edit Recurring Event' : 'New Recurring Event'}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Templates appear each week in the Recurring Events checklist for quick one-click insertion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input
                value={recurringForm.name}
                onChange={(e) => setRecurringForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Mens League"
                data-testid="wes-recurring-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Day of Week</Label>
                <Select
                  value={String(recurringForm.day_index)}
                  onValueChange={(v) => setRecurringForm(f => ({ ...f, day_index: Number(v) }))}
                >
                  <SelectTrigger data-testid="wes-recurring-form-day"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAY_NAMES.map((dn, di) => (
                      <SelectItem key={di} value={String(di)}>{dn}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Guest Count</Label>
                <Input
                  type="number"
                  value={recurringForm.guest_count}
                  onChange={(e) => setRecurringForm(f => ({ ...f, guest_count: e.target.value }))}
                  data-testid="wes-recurring-form-guests"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start Time</Label>
                <Input
                  type="time"
                  value={recurringForm.time_start}
                  onChange={(e) => setRecurringForm(f => ({ ...f, time_start: e.target.value }))}
                  data-testid="wes-recurring-form-start"
                />
              </div>
              <div>
                <Label className="text-xs">End Time</Label>
                <Input
                  type="time"
                  value={recurringForm.time_end}
                  onChange={(e) => setRecurringForm(f => ({ ...f, time_end: e.target.value }))}
                  data-testid="wes-recurring-form-end"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Room</Label>
                <Select
                  value={recurringForm.room || '__none__'}
                  onValueChange={(v) => setRecurringForm(f => ({ ...f, room: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger data-testid="wes-recurring-form-room"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {ROOMS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Service Style</Label>
                <Select
                  value={recurringForm.service_style || '__none__'}
                  onValueChange={(v) => setRecurringForm(f => ({ ...f, service_style: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger data-testid="wes-recurring-form-style"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {SERVICE_STYLES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={2}
                value={recurringForm.notes}
                onChange={(e) => setRecurringForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="wes-recurring-form-notes"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={recurringForm.active !== false}
                onChange={(e) => setRecurringForm(f => ({ ...f, active: e.target.checked }))}
                data-testid="wes-recurring-form-active"
              />
              Active (show in checklist)
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => { setShowManageRecurring(false); setEditingRecurringId(null); setRecurringForm(emptyRecurringForm); }}
              data-testid="wes-recurring-form-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveRecurring}
              disabled={savingRecurring}
              className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
              data-testid="wes-recurring-form-save"
            >
              {savingRecurring ? (<><Loader2 size={14} className="animate-spin mr-1" />Saving…</>) : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WeeklyEventSheet;
